import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { classNames } from "primereact/utils";
import { AppMenuItem } from "../../types/layout";
import { useSidebar } from "../../contexts/SidebarContext";
import {
    getMenuChildren,
    isRouteMatch,
    itemHasDropdown,
} from "../../utils/menuNavigation";
import AppSidebarFlyoutPanel from "./AppSidebarFlyoutPanel";

type AppSidebarItemProps = {
    item: AppMenuItem;
    itemKey: string;
    activeMenuKey: string | null;
    sectionLabel: string;
    depth?: number;
};

const HOVER_CLOSE_DELAY = 140;

const AppSidebarItem = ({
    item,
    itemKey,
    activeMenuKey,
    sectionLabel,
    depth = 0,
}: AppSidebarItemProps) => {
    const { pathname } = useLocation();
    const { isMini, closeMobile } = useSidebar();
    const itemRef = useRef<HTMLLIElement>(null);
    const subRef = useRef<HTMLUListElement>(null);
    const closeTimerRef = useRef<number | null>(null);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [open, setOpen] = useState(false);
    const [flyoutOpen, setFlyoutOpen] = useState(false);

    const children = getMenuChildren(item);
    const hasDropdown = itemHasDropdown(item);
    const isActive =
        activeMenuKey === itemKey ||
        Boolean(activeMenuKey?.startsWith(`${itemKey}-`));
    const isCurrent = item.to ? isRouteMatch(pathname, item.to) : false;

    useEffect(() => {
        if (!isMini && isActive && hasDropdown) {
            setOpen(true);
        }
    }, [hasDropdown, isActive, isMini, itemKey]);

    useEffect(() => {
        if (!isMini && open && subRef.current) {
            const menuContainer = subRef.current.closest(
                ".app-sidebar__menu"
            ) as HTMLElement | null;

            if (!menuContainer) {
                return;
            }

            requestAnimationFrame(() => {
                if (!subRef.current) {
                    return;
                }

                const subRect = subRef.current.getBoundingClientRect();
                const menuRect = menuContainer.getBoundingClientRect();
                const bottomOverflow = subRect.bottom - menuRect.bottom;

                if (bottomOverflow > 0) {
                    menuContainer.scrollTop += bottomOverflow + 8;
                }
            });
        }
    }, [isMini, open]);

    useEffect(() => {
        return () => {
            if (closeTimerRef.current) {
                window.clearTimeout(closeTimerRef.current);
            }
        };
    }, []);

    const handleNavigate = () => {
        closeMobile();
        setFlyoutOpen(false);
    };

    const openFlyout = () => {
        if (closeTimerRef.current) {
            window.clearTimeout(closeTimerRef.current);
            closeTimerRef.current = null;
        }
        setAnchorEl(itemRef.current);
        setFlyoutOpen(true);
    };

    const scheduleCloseFlyout = () => {
        if (closeTimerRef.current) {
            window.clearTimeout(closeTimerRef.current);
        }

        closeTimerRef.current = window.setTimeout(() => {
            setFlyoutOpen(false);
            closeTimerRef.current = null;
        }, HOVER_CLOSE_DELAY);
    };

    const iconClass =
        item.icon ||
        (depth === 0 ? "pi pi-fw pi-circle" : "pi pi-fw pi-angle-right");

    const flyoutContent = (
        <>
            <div className="app-sidebar-flyout__section">{sectionLabel}</div>
            {hasDropdown ? (
                <>
                    <div className="app-sidebar-flyout__title">{item.label}</div>
                    <ul className="app-sidebar-flyout__list">
                        {children.map((child, index) => (
                            <AppSidebarFlyoutItem
                                key={child.key ?? `${itemKey}-${index}`}
                                item={child}
                                parentKey={`${itemKey}-${index}`}
                                activeMenuKey={activeMenuKey}
                                onNavigate={handleNavigate}
                            />
                        ))}
                    </ul>
                </>
            ) : item.to ? (
                <Link
                    to={item.to}
                    onClick={handleNavigate}
                    className={classNames("app-sidebar-flyout__single", {
                        "is-current": isCurrent,
                    })}
                >
                    {item.label}
                </Link>
            ) : (
                <div className="app-sidebar-flyout__title">{item.label}</div>
            )}
        </>
    );

    if (isMini) {
        return (
            <>
                <li
                    ref={itemRef}
                    className={classNames("app-sidebar-item", {
                        "is-active": isActive || isCurrent,
                        "has-children": hasDropdown,
                        "is-flyout-open": flyoutOpen,
                    })}
                    onMouseEnter={openFlyout}
                    onMouseLeave={scheduleCloseFlyout}
                >
                    {item.to && !hasDropdown ? (
                        <Link
                            to={item.to}
                            className="app-sidebar-link app-sidebar-link--icon"
                            onClick={handleNavigate}
                            title={`${sectionLabel}: ${item.label}`}
                        >
                            <i
                                className={classNames(
                                    "layout-menuitem-icon app-sidebar-link__icon",
                                    iconClass
                                )}
                            />
                        </Link>
                    ) : (
                        <span
                            className="app-sidebar-link app-sidebar-link--icon"
                            title={`${sectionLabel}: ${item.label}`}
                        >
                            <i
                                className={classNames(
                                    "layout-menuitem-icon app-sidebar-link__icon",
                                    iconClass
                                )}
                            />
                        </span>
                    )}
                </li>

                <AppSidebarFlyoutPanel
                    anchorEl={anchorEl}
                    visible={flyoutOpen}
                    onMouseEnter={openFlyout}
                    onMouseLeave={scheduleCloseFlyout}
                >
                    {flyoutContent}
                </AppSidebarFlyoutPanel>
            </>
        );
    }

    return (
        <li
            ref={itemRef}
            className={classNames("app-sidebar-item", {
                "is-active": isActive || isCurrent,
                "is-open": open,
                "has-children": hasDropdown,
            })}
        >
            {hasDropdown ? (
                <button
                    type="button"
                    className="app-sidebar-link"
                    onClick={() => setOpen((value) => !value)}
                    aria-expanded={open}
                >
                    <i
                        className={classNames(
                            "layout-menuitem-icon app-sidebar-link__icon",
                            iconClass
                        )}
                    />
                    <span className="app-sidebar-link__label layout-menuitem-text">{item.label}</span>
                    <i className="pi pi-fw pi-angle-down layout-submenu-toggler app-sidebar-link__chevron" />
                </button>
            ) : (
                <Link
                    to={item.to!}
                    className={classNames("app-sidebar-link", {
                        "is-current": isCurrent,
                    })}
                    onClick={handleNavigate}
                >
                    <i
                        className={classNames(
                            "layout-menuitem-icon app-sidebar-link__icon",
                            iconClass
                        )}
                    />
                    <span className="app-sidebar-link__label layout-menuitem-text">{item.label}</span>
                </Link>
            )}

            {hasDropdown && open && (
                <ul ref={subRef} className="app-sidebar-sub">
                    {children.map((child, index) => (
                        <AppSidebarItem
                            key={child.key ?? `${itemKey}-${index}`}
                            item={child}
                            itemKey={`${itemKey}-${index}`}
                            activeMenuKey={activeMenuKey}
                            sectionLabel={sectionLabel}
                            depth={depth + 1}
                        />
                    ))}
                </ul>
            )}
        </li>
    );
};

type FlyoutItemProps = {
    item: AppMenuItem;
    parentKey: string;
    activeMenuKey: string | null;
    onNavigate: () => void;
};

const AppSidebarFlyoutItem = ({
    item,
    parentKey,
    activeMenuKey,
    onNavigate,
}: FlyoutItemProps) => {
    const { pathname } = useLocation();
    const itemRef = useRef<HTMLLIElement>(null);
    const closeTimerRef = useRef<number | null>(null);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [nestedOpen, setNestedOpen] = useState(false);
    const children = getMenuChildren(item);
    const hasDropdown = itemHasDropdown(item);
    const isCurrent = item.to ? isRouteMatch(pathname, item.to) : false;
    const isActive =
        activeMenuKey === parentKey ||
        Boolean(activeMenuKey?.startsWith(`${parentKey}-`));

    const openNested = () => {
        if (closeTimerRef.current) {
            window.clearTimeout(closeTimerRef.current);
            closeTimerRef.current = null;
        }
        setAnchorEl(itemRef.current);
        setNestedOpen(true);
    };

    const scheduleCloseNested = () => {
        if (closeTimerRef.current) {
            window.clearTimeout(closeTimerRef.current);
        }

        closeTimerRef.current = window.setTimeout(() => {
            setNestedOpen(false);
            closeTimerRef.current = null;
        }, HOVER_CLOSE_DELAY);
    };

    useEffect(() => {
        return () => {
            if (closeTimerRef.current) {
                window.clearTimeout(closeTimerRef.current);
            }
        };
    }, []);

    if (hasDropdown) {
        return (
            <>
                <li
                    ref={itemRef}
                    className={classNames("app-sidebar-flyout__item has-children", {
                        "is-active": isActive,
                        "is-flyout-open": nestedOpen,
                    })}
                    onMouseEnter={openNested}
                    onMouseLeave={scheduleCloseNested}
                >
                    <span className="app-sidebar-flyout__parent">
                        {item.label}
                        <i className="pi pi-angle-right" />
                    </span>
                </li>

                <AppSidebarFlyoutPanel
                    anchorEl={anchorEl}
                    visible={nestedOpen}
                    onMouseEnter={openNested}
                    onMouseLeave={scheduleCloseNested}
                >
                    <ul className="app-sidebar-flyout__list">
                        {children.map((child, index) => (
                            <AppSidebarFlyoutItem
                                key={child.key ?? `${parentKey}-${index}`}
                                item={child}
                                parentKey={`${parentKey}-${index}`}
                                activeMenuKey={activeMenuKey}
                                onNavigate={onNavigate}
                            />
                        ))}
                    </ul>
                </AppSidebarFlyoutPanel>
            </>
        );
    }

    return (
        <li
            className={classNames("app-sidebar-flyout__item", {
                "is-current": isCurrent,
            })}
        >
            <Link
                to={item.to!}
                onClick={onNavigate}
                className="layout-menuitem-text"
            >
                {item.label}
            </Link>
        </li>
    );
};

export default AppSidebarItem;
